import { ActivityIndicator, Platform } from "react-native";
import styled, { css, DefaultTheme, useTheme } from "styled-components/native";
import { ButtonCategory, ButtonProps, ButtonType } from "../Button/types";
import { getStyleForCategory } from "../Button/utils";
import VDBText from "../Text";
import VDBIcon from "../../icons/VDBIcon";
import { IconsVariant } from "../../icons/VDBIcon";
import Spacer from "../Spacer";
import { SpaceType } from "../Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useState } from "react";
import { LinearGradient } from "expo-linear-gradient";
import { useGradientFromValue } from "../../../hooks/useThemedGradient";
import { useSelector } from "react-redux";

const Button = ({
  category = ButtonCategory.default,
  type = ButtonType.primary,
  title,
  disabled = false,
  loading,
  onPress,
  testID,
  dataTestId,
  showIcon = false,
  iconSource = undefined,
  height,
  width,
  borderRadius,
  iconColor,
  iconSize,
  paddingLeftText,
  textTypographyStyle,
  hideText,
  showLeftText,
  isDisableInteraction = false,
  alignIconInLeft = false,
  maxWidth,
  solidBackground = false,
}: ButtonProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const [hovered, setHovered] = useState<boolean>(false);
  const enableGradient =
    useSelector(
      (state: any) => state.globalData?.app_settings?.enable_gradient
    ) ?? false;
  const { textColor, background, borderColor, hover } = getStyleForCategory(
    enableGradient ? ButtonCategory.default : category,
    type,
    disabled
  );

  const currentBgToken = hovered ? hover : background;
  const bgValue = theme[currentBgToken as keyof DefaultTheme] as
    | string
    | undefined;
  const fallbackColor = theme["core-01"] as string;
  const gradient = useGradientFromValue(bgValue, fallbackColor);
  return (
    <CustomButton
      onPress={onPress}
      disabled={disabled || loading || isDisableInteraction}
      testID={testID}
      data-testId={dataTestId}
      textColor={textColor}
      background={background}
      borderColor={borderColor}
      borderRadius={borderRadius}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      height={height}
      width={width}
      maxWidth={maxWidth}
      type={type}
      accessibilityRole="button"
      accessibilityState={{ disabled: disabled || loading }}
      accessibilityLabel={title}
      focusColor={theme["link-hover"] as string}
      solidBackground={solidBackground && !enableGradient}
      {...(!enableGradient && {
        style: ({ pressed }: { pressed: boolean }) => [
          {
            backgroundColor:
              pressed || hovered
                ? (theme[hover as keyof DefaultTheme] as string)
                : (theme[background as keyof DefaultTheme] as string),
          },
        ],
      })}
    >
      {enableGradient && (
        <GradientOverlay
          colors={gradient.colors}
          start={gradient.start}
          end={gradient.end}
          locations={gradient.locations}
          borderRadius={borderRadius}
        />
      )}
      {loading ? (
        <ActivityIndicator
          color={theme[textColor as keyof DefaultTheme] as string}
        />
      ) : showIcon ? (
        alignIconInLeft ? (
          <IconContainerWithLeftIcon>
            <VDBIcon
              color={iconColor ? iconColor : theme["main-01"]}
              size={iconSize ? iconSize : 24}
              variant={IconsVariant.VDBCustom}
              name={iconSource}
            />
            <Spacer size={SpaceType.Double} />
            <VDBText
              color={theme[textColor as keyof DefaultTheme] as string}
              type={typography[textTypographyStyle as string]}
            >
              {title}
            </VDBText>
          </IconContainerWithLeftIcon>
        ) : (
          <IconButtonContainer paddingLeft={!hideText ? paddingLeftText : 0}>
            {!hideText && (
              <>
                <VDBText
                  color={theme[textColor as keyof DefaultTheme] as string}
                  type={typography[textTypographyStyle as string]}
                >
                  {title}
                </VDBText>

                <Spacer size={SpaceType.Double} />
              </>
            )}

            <VDBIcon
              color={
                iconColor
                  ? iconColor
                  : (theme[textColor as keyof DefaultTheme] as string)
              }
              size={iconSize ? iconSize : 24}
              variant={IconsVariant.VDBCustom}
              name={iconSource}
            />

            {showLeftText && (
              <>
                <Spacer size={SpaceType.Double} />
                <VDBText
                  color={theme[textColor as keyof DefaultTheme] as string}
                  type={typography[textTypographyStyle as string]}
                >
                  {title}
                </VDBText>
              </>
            )}
          </IconButtonContainer>
        )
      ) : (
        <VDBText
          color={theme[textColor as keyof DefaultTheme] as string}
          isBold={true}
          type={typography["H3-M"]}
        >
          {title}
        </VDBText>
      )}
    </CustomButton>
  );
};

const GradientOverlay = styled(LinearGradient)<{ borderRadius?: number }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: ${({ borderRadius }: { borderRadius?: number }) =>
    borderRadius && borderRadius > 0 ? borderRadius : 8}px;
`;

const CustomButton = styled.Pressable<any>`
  justify-content: center;
  align-items: center;
  width: ${({ width }: { width?: number }) => (width ? `${width}` : "100%")};
  max-width: ${({ maxWidth }: { maxWidth?: number }) =>
    maxWidth ? `${maxWidth}px` : "100%"};
  padding-horizontal: ${SpaceType.Double}px;
  height: ${({ height }: { height?: number }) =>
    height && height > 0 ? height : 40}px;
  border-radius: ${({ borderRadius }: { borderRadius?: number }) =>
    borderRadius && borderRadius > 0 ? borderRadius : 8}px;
  border-width: ${({ type }: { type?: ButtonType }) =>
    type === ButtonType.secondary ? 2 : 0}px;
  border-color: ${({
    borderColor,
    theme,
  }: {
    borderColor?: string;
    theme: DefaultTheme;
  }) => theme[borderColor as keyof DefaultTheme]};

  ${({ focusColor }: { focusColor?: string }) =>
    Platform.OS === "web" &&
    focusColor &&
    css`
      &:focus {
        outline: none;
      }
      &:focus-visible {
        outline: 2px solid ${focusColor};
        outline-offset: 2px;
      }
    `}
  ${({
    background,
    solidBackground,
    theme,
  }: {
    background?: string;
    solidBackground?: boolean;
    theme: DefaultTheme;
  }) =>
    solidBackground && background && background !== "transparent"
      ? css`
          background-color: ${theme[
            background as keyof DefaultTheme
          ] as string};
        `
      : css``};
`;

const IconButtonContainer = styled.View<any>`
  flex-direction: row;
  flex: 1;
  justify-content: center;
  padding-left: ${({ paddingLeft }: { paddingLeft?: number }) =>
    paddingLeft && paddingLeft > 0 ? paddingLeft : 0}px;
  align-items: center;
`;

const IconContainerWithLeftIcon = styled.View`
  flex-direction: row;
  flex: 1;
  justify-content: center;
  align-items: center;
`;

export default Button;
